/** @jsxImportSource @emotion/react */
import { useMemo } from 'react'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import {
  useGetLabOfficesQuery,
  useGetOfficeSettingsQuery,
  useUpdateOfficeSettingsMutation,
} from 'store/services/office'

interface IProps {
  officeId: number
}

type OfficeOption = {
  value: number | 'none' | 'separator'
  label: string
  isDisabled?: boolean
}

const NONE_OPTION: OfficeOption = { value: 'none', label: 'None' }
const SEPARATOR: OfficeOption = { value: 'separator' as any, label: '─────────────────', isDisabled: true }

export const OfficePreferredDmeOfficeSection = ({ officeId }: IProps) => {
  const { data: offices = [], isLoading: isLoadingOffices } = useGetLabOfficesQuery({})
  const { data: officeSettings } = useGetOfficeSettingsQuery({ id: officeId })
  const [updateOfficeSettings, { isLoading: isUpdating }] = useUpdateOfficeSettingsMutation()

  const officeOptions = useMemo<OfficeOption[]>(
    () =>
      offices
        .filter((office) => office.isDmeOffice && office.officeName)
        .sort((left, right) => String(left.officeName).localeCompare(String(right.officeName)))
        .map((office) => ({
          value: office.id,
          label: `${office.officeName} (${office.addressCity}, ${office.addressState})`,
        })),
    [offices],
  )

  const allOptions: OfficeOption[] = [NONE_OPTION, SEPARATOR, ...officeOptions]

  const selectedValue: OfficeOption | null = officeSettings?.isPreferredDmeNone
    ? NONE_OPTION
    : officeOptions.find(
        (option) => Number(option.value) === Number(officeSettings?.preferredDmeOfficeId),
      ) ?? null

  const handlePreferredDmeChange = async (option: OfficeOption | null) => {
    const isNone = option?.value === 'none'
    try {
      await updateOfficeSettings({
        id: officeId,
        preferredDmeOfficeId: isNone ? null : (option?.value as number) ?? null,
        isPreferredDmeNone: isNone,
      }).unwrap()
      toast.success(
        isNone
          ? 'Office preferred DME set to None.'
          : option
            ? 'Office preferred DME office updated.'
            : 'Office preferred DME office cleared.',
      )
    } catch (error) {
      console.error('Error updating office preferred DME office:', error)
      toast.error('Failed to update office preferred DME office.')
    }
  }

  return (
    <div className="form-group textGray mt-3">
      <label htmlFor="officePreferredDmeOffice">Preferred DME Office</label>
      <ReactSelect
        inputId="officePreferredDmeOffice"
        className="basic-single"
        classNamePrefix="select"
        isClearable
        isSearchable
        isLoading={isLoadingOffices || isUpdating}
        placeholder="Search DME offices..."
        value={selectedValue}
        onChange={(option) => handlePreferredDmeChange(option as OfficeOption | null)}
        options={allOptions}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
      />
    </div>
  )
}
